import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Input,
  Button,
  Heading,
  Text,
  Label,
  HelperText,
  ErrorText,
  Select,
  MultiSelectComboBox,
} from '../ui';
import { plantApi } from '../../lib/plantApi';
import { PlantVariety, Genus, Species } from '../../types/plant';
import { useDebounce } from '../../hooks/useDebounce';
import {
  useCreateCompleteVariety,
  useUpdatePlantVariety,
} from '../../hooks/usePlantVarietyMutations';
import { GenusSearch } from './GenusSearch';
import { SpeciesSearch } from './SpeciesSearch';

// Badge-relevant tags for plant varieties
const BADGE_TAGS = [
  'native',
  'heirloom',
  'rare',
  'endangered',
  'threatened',
  'heritage',
  'historic',
  'unusual',
  'exotic',
  'experimental',
  'shared',
  'donated',
  'seed-swap',
  'award-winner',
  'record-holder',
] as const;

const addVarietySchema = z.object({
  primaryCommonName: z.string().min(1, 'Common name is required').max(255, 'Common name too long'),
  genusName: z.string().min(1, 'Genus is required').max(100, 'Genus name too long'),
  speciesName: z.string().min(1, 'Species is required').max(100, 'Species name too long'),
  subspecies: z.string().max(100, 'Subspecies too long').optional(),
  cultivarName: z.string().max(255, 'Cultivar name too long').optional(),
  plantType: z.string().max(50, 'Plant type too long').optional(),
  growthPattern: z.enum(['annual', 'perennial', 'biennial']).optional(),
  tags: z.array(z.enum(BADGE_TAGS)).optional(),
});

type AddVarietyFormData = z.infer<typeof addVarietySchema>;

interface AddPlantVarietyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (variety: PlantVariety) => void;
  initialSearchQuery?: string;
  editingVariety?: PlantVariety | null;
}

type ModalStep = 'search' | 'manual' | 'form';

export function AddPlantVarietyModal({
  isOpen,
  onClose,
  onSuccess,
  initialSearchQuery = '',
  editingVariety = null,
}: AddPlantVarietyModalProps) {
  const isEditing = !!editingVariety;
  const [currentStep, setCurrentStep] = useState<ModalStep>(isEditing ? 'form' : 'search');
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [searchResults, setSearchResults] = useState<PlantVariety[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<PlantVariety | null>(null);
  const [selectedGenus, setSelectedGenus] = useState<Genus | null>(
    editingVariety?.species.genus || null
  );
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(
    editingVariety?.species || null
  );

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const createCompleteVarietyMutation = useCreateCompleteVariety();
  const updatePlantVarietyMutation = useUpdatePlantVariety();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<AddVarietyFormData>({
    resolver: zodResolver(addVarietySchema),
    defaultValues: {
      primaryCommonName: editingVariety?.primaryCommonName || '',
      genusName: editingVariety?.species.genus.name || '',
      speciesName: editingVariety?.species.name || '',
      subspecies: editingVariety?.subspecies || '',
      cultivarName: editingVariety?.cultivarName || '',
      plantType: editingVariety?.plantType || '',
      growthPattern: editingVariety?.growthPattern || undefined,
      tags: editingVariety?.tags as (typeof BADGE_TAGS)[number][] | undefined,
    },
  });

  // Search for existing varieties when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery.trim().length >= 2 && currentStep === 'search') {
      setIsSearching(true);
      plantApi
        .searchPlantVarieties(debouncedSearchQuery.trim())
        .then(varieties => {
          setSearchResults(varieties || []);
        })
        .catch(() => {
          setSearchResults([]);
        })
        .finally(() => {
          setIsSearching(false);
        });
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchQuery, currentStep]);

  const onSubmit = async (data: AddVarietyFormData) => {
    try {
      if (isEditing && editingVariety) {
        // Update existing variety
        const varietyData = {
          primaryCommonName: data.primaryCommonName,
          subspecies: data.subspecies || null,
          cultivarName: data.cultivarName || null,
          alternativeNames: editingVariety.alternativeNames || [],
          plantType: data.plantType || null,
          growthPattern: data.growthPattern || null,
          tags: data.tags ?? [],
        };

        const updatedVariety = await updatePlantVarietyMutation.mutateAsync({
          id: editingVariety.id.toString(),
          varietyData,
        });

        if (onSuccess) {
          onSuccess(updatedVariety);
        }
      } else {
        // Create new variety
        const varietyData = {
          genusName: data.genusName,
          speciesName: data.speciesName,
          primaryCommonName: data.primaryCommonName,
          subspecies: data.subspecies || null,
          cultivarName: data.cultivarName || null,
          alternativeNames: [],
          plantType: data.plantType || null,
          growthPattern: data.growthPattern || null,
          tags: data.tags ?? [],
        };

        const newVariety = await createCompleteVarietyMutation.mutateAsync(varietyData);

        if (onSuccess) {
          onSuccess(newVariety);
        }
      }

      handleClose();
    } catch {
      // Error is handled by mutation error state
    }
  };

  const handleClose = () => {
    reset();
    setCurrentStep(isEditing ? 'form' : 'search');
    setSearchQuery(initialSearchQuery);
    setSearchResults([]);
    setSelectedTemplate(null);
    onClose();
  };

  // Initialize search query if provided
  useEffect(() => {
    if (initialSearchQuery && isOpen) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery, isOpen]);

  const handleUseTemplate = (variety: PlantVariety) => {
    setSelectedTemplate(variety);
    setValue('primaryCommonName', '');
    setValue('genusName', variety.species.genus.name);
    setValue('speciesName', variety.species.name);
    setValue('plantType', variety.plantType || '');
    setCurrentStep('form');
  };

  const isCreating = createCompleteVarietyMutation.isPending;
  const isUpdating = updatePlantVarietyMutation.isPending;
  const isSubmitting = isCreating || isUpdating;

  return (
    <Transition show={isOpen}>
      <Dialog className="relative z-50" onClose={handleClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all border border-gray-200 dark:border-gray-700">
                <DialogTitle className="flex items-center justify-between mb-6">
                  <div>
                    <Heading as="h2" size="sm">
                      {isEditing ? 'Edit Plant Variety' : 'Add New Plant Variety'}
                    </Heading>
                    <Text variant="muted" size="sm" className="mt-1">
                      {!isEditing &&
                        currentStep === 'search' &&
                        'Step 1: Find a similar plant or create from scratch'}
                      {!isEditing &&
                        currentStep === 'manual' &&
                        'Step 2: Enter botanical information manually'}
                      {currentStep === 'form' &&
                        (isEditing
                          ? 'Update variety details'
                          : 'Step 3: Complete your plant variety details')}
                    </Text>
                  </div>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </DialogTitle>

                {/* Step 1: Search for similar plants */}
                {!isEditing && currentStep === 'search' && (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <Heading as="h3" size="xs" className="text-green-900 mb-3">
                        🔍 Find a similar plant
                      </Heading>
                      <Text className="text-green-800 mb-4">
                        Search for plants similar to yours to auto-fill botanical information
                      </Text>

                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-green-600" />
                        <Input
                          type="text"
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          placeholder="Try 'tomato', 'rose', 'basil'..."
                          className="pl-10 bg-white"
                        />
                        {isSearching && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                          </div>
                        )}
                      </div>

                      {/* Search Results */}
                      {searchResults.length > 0 && (
                        <div className="mt-4">
                          <Text variant="muted" size="sm" className="mb-2">
                            Click one to use as a starting point:
                          </Text>
                          <div className="max-h-48 overflow-y-auto border border-green-200 rounded-md bg-white">
                            {searchResults.map(variety => (
                              <button
                                key={variety.id}
                                type="button"
                                onClick={() => handleUseTemplate(variety)}
                                className="w-full px-4 py-3 text-left hover:bg-green-50 border-b border-green-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900">
                                  {variety.primaryCommonName}
                                </div>
                                <div className="text-sm text-green-700">
                                  <span className="italic">
                                    {variety.species.genus.name} {variety.species.name}
                                  </span>
                                  {variety.plantType && (
                                    <span className="ml-2">• {variety.plantType}</span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                        <div className="mt-4 p-3 text-sm text-green-700 bg-green-100 rounded-md">
                          No similar plants found. You can create from scratch below.
                        </div>
                      )}
                    </div>

                    <div className="text-center">
                      <Text variant="muted" className="mb-3">
                        or
                      </Text>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentStep('manual')}
                        className="w-full"
                      >
                        Create from scratch (enter botanical names manually)
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 2: Manual entry */}
                {!isEditing && currentStep === 'manual' && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <Heading as="h3" size="xs" className="text-blue-900 mb-3">
                        📝 Enter botanical information
                      </Heading>
                      <Text className="text-blue-800 mb-4">
                        Fill in the botanical classification for your plant
                      </Text>

                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="manual-genus" required>
                            Genus
                          </Label>
                          <GenusSearch
                            value={selectedGenus}
                            onChange={genus => {
                              setSelectedGenus(genus);
                              setValue('genusName', genus?.name || '');
                              // Reset species when genus changes
                              if (selectedSpecies && selectedSpecies.genus?.id !== genus?.id) {
                                setSelectedSpecies(null);
                                setValue('speciesName', '');
                              }
                            }}
                            error={errors.genusName?.message}
                            placeholder="Search for a genus..."
                            onCreateNew={async genusName => {
                              try {
                                const newGenus = await plantApi.createGenus(genusName);
                                setSelectedGenus(newGenus);
                                setValue('genusName', newGenus.name);
                              } catch {
                                // TODO: Show user-friendly error message
                                // For now, silently fail - user can try again
                              }
                            }}
                          />
                        </div>
                        <div>
                          <Label htmlFor="manual-species" required>
                            Species
                          </Label>
                          <SpeciesSearch
                            value={selectedSpecies}
                            onChange={species => {
                              setSelectedSpecies(species);
                              setValue('speciesName', species?.name || '');
                            }}
                            genus={selectedGenus}
                            error={errors.speciesName?.message}
                            placeholder="Search for a species..."
                            onCreateNew={async (speciesName, genus) => {
                              try {
                                const newSpecies = await plantApi.createSpecies({
                                  name: speciesName,
                                  genusId: genus.id,
                                });
                                setSelectedSpecies(newSpecies);
                                setValue('speciesName', newSpecies.name);
                              } catch {
                                // TODO: Show user-friendly error message
                                // For now, silently fail - user can try again
                              }
                            }}
                          />
                        </div>
                      </div>

                      <div className="mt-4 flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setCurrentStep('search')}
                          className="flex-1"
                        >
                          Back to Search
                        </Button>
                        <Button
                          onClick={() => setCurrentStep('form')}
                          className="flex-1"
                          disabled={!selectedGenus || !selectedSpecies}
                        >
                          Continue
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Final form */}
                {currentStep === 'form' && (
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <Heading as="h3" size="xs" className="text-purple-900 mb-3">
                        {isEditing ? '✏️ Edit variety details' : '✨ Complete your variety'}
                      </Heading>

                      {!isEditing && selectedTemplate && (
                        <div className="bg-white rounded-md p-3 mb-4 border border-purple-200">
                          <Text variant="muted" size="sm" className="mb-1">
                            Using template from:
                          </Text>
                          <Text className="font-medium">{selectedTemplate.primaryCommonName}</Text>
                          <Text variant="muted" size="sm">
                            <span className="italic">
                              {selectedTemplate.species.genus.name} {selectedTemplate.species.name}
                            </span>
                          </Text>
                        </div>
                      )}

                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="primaryCommonName" required>
                            What do you call this plant?
                          </Label>
                          <Input
                            id="primaryCommonName"
                            placeholder="e.g., My Cherry Tomatoes, Mom's Roses"
                            error={errors.primaryCommonName?.message}
                            {...register('primaryCommonName')}
                          />
                          <HelperText>Give it a name that makes sense to you</HelperText>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="genusDisplay">Genus</Label>
                            <Input
                              id="genusDisplay"
                              value={watch('genusName')}
                              disabled
                              className="bg-gray-50"
                            />
                            {isEditing && (
                              <HelperText>Genus cannot be changed when editing</HelperText>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="speciesDisplay">Species</Label>
                            <Input
                              id="speciesDisplay"
                              value={watch('speciesName')}
                              disabled
                              className="bg-gray-50"
                            />
                            {isEditing && (
                              <HelperText>Species cannot be changed when editing</HelperText>
                            )}
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="cultivarName">
                            Variety/Cultivar{' '}
                            <Text as="span" variant="muted">
                              (optional)
                            </Text>
                          </Label>
                          <Input
                            id="cultivarName"
                            placeholder="e.g., Cherokee Purple, Beefsteak"
                            {...register('cultivarName')}
                          />
                        </div>

                        <div>
                          <Label htmlFor="plantType">Plant Type</Label>
                          <Select
                            id="plantType"
                            placeholder="Choose one..."
                            {...register('plantType')}
                          >
                            <option value="vegetable">Vegetable</option>
                            <option value="herb">Herb</option>
                            <option value="flower">Flower</option>
                            <option value="fruit">Fruit</option>
                            <option value="tree">Tree</option>
                            <option value="shrub">Shrub</option>
                            <option value="succulent">Succulent</option>
                            <option value="houseplant">Houseplant</option>
                            <option value="other">Other</option>
                          </Select>
                        </div>

                        <div>
                          <Label htmlFor="growthPattern">
                            Growth Pattern{' '}
                            <Text as="span" variant="muted">
                              (optional)
                            </Text>
                          </Label>
                          <Select
                            id="growthPattern"
                            placeholder="Choose growth pattern..."
                            {...register('growthPattern')}
                          >
                            <option value="annual">Annual</option>
                            <option value="perennial">Perennial</option>
                            <option value="biennial">Biennial</option>
                          </Select>
                        </div>

                        <div>
                          <Label htmlFor="tags">
                            Tags{' '}
                            <Text as="span" variant="muted">
                              (optional)
                            </Text>
                          </Label>
                          <MultiSelectComboBox
                            value={watch('tags') || []}
                            onChange={value => setValue('tags', value)}
                            options={BADGE_TAGS.map(tag => ({
                              value: tag,
                              label: tag.charAt(0).toUpperCase() + tag.slice(1).replace('-', ' '),
                            }))}
                            placeholder="Select tags for this variety..."
                            searchable={true}
                            className="w-full"
                          />
                          <HelperText>
                            Tag special characteristics to help with badge achievements
                          </HelperText>
                        </div>
                      </div>

                      <div className="mt-6 flex gap-3">
                        {!isEditing && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setCurrentStep(selectedTemplate ? 'search' : 'manual')}
                            disabled={isSubmitting}
                            className="flex-1"
                          >
                            Back
                          </Button>
                        )}
                        <Button
                          type="submit"
                          isLoading={isSubmitting}
                          disabled={isSubmitting}
                          className={isEditing ? 'w-full' : 'flex-1'}
                        >
                          {isSubmitting
                            ? isEditing
                              ? 'Updating...'
                              : 'Creating...'
                            : isEditing
                              ? 'Update Variety'
                              : 'Create Variety'}
                        </Button>
                      </div>
                    </div>

                    {/* Error Display */}
                    {(createCompleteVarietyMutation.isError ||
                      updatePlantVarietyMutation.isError) && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                        <ErrorText>
                          {createCompleteVarietyMutation.error?.message ||
                            updatePlantVarietyMutation.error?.message ||
                            `Failed to ${isEditing ? 'update' : 'create'} plant variety`}
                        </ErrorText>
                      </div>
                    )}
                  </form>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
